import { Box, Flex, Grid, Text } from "@chakra-ui/layout";
import PageTitle from "../components/PageTitle";
import InputSingleDatePicker from "../components/inputs/InputSingleDatePicker";
import { useEffect, useMemo, useRef, useState } from "react";
import { Chart } from "chart.js/auto";
import { Command, CommandStatus, CommandType } from "../types/command.types";
import {
  useCommandsCentersStats,
  useCommandsEarmoldTypesStats,
  useCommandsStatusStats,
  useCommandsTreatmentTimeStats,
  useCommandsTypesStats,
  useTicketsTypesStats,
} from "../hooks/stats.hooks";
import { NavLink, useNavigate } from "react-router-dom";
import { Icon } from "@chakra-ui/icon";
import {
  CommandTreatmentTimeStats,
  StatsFilterProps,
} from "../types/stats.types";
import { useConnectedUser } from "../hooks/user.hooks";
import { useGlobalAnnouncement } from "../hooks/announcements.hooks";
import {
  useCenterCommandsEarmoldTypesStats,
  useCenterCommandsPaginated,
  useCenterCommandsStatusStats,
  useCenterPatientsStats,
  useCenters,
  useCenterTicketsTypesStats,
} from "../hooks/centers.hooks";
import ClientSideTable from "../components/table/ClientSideTable";
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import OrderStatusBadge from "../components/OrderStatusBadge";
import CommandTypeBadge from "../components/CommandTypeBadge";
import { format } from "date-fns";
import InlineUserAvatar from "../components/InlineUserAvatar";
import Tooltip from "../components/Tooltip";
import { useMediaQuery } from "@uidotdev/usehooks";
import { Button } from "@chakra-ui/react";
import DashboardCardWithHeader from "../components/DashboardCartWithHeader";
import DashboardCard from "../components/DashboardCard";
import i18n from "../i18n";

const PieChartCenterTextPlugin = (
  text: string | ((chart: Chart) => string)
) => ({
  id: "text",
  beforeDraw: (chart: Chart) => {
    const ctx = chart.ctx;
    ctx.restore();
    const fontSize = (chart.height / 114).toFixed(2);
    ctx.font = fontSize + "em sans-serif";
    ctx.textBaseline = "middle";
    const t = typeof text === "string" ? text : text(chart);
    const textX = Math.round((chart.width - ctx.measureText(t).width) / 2);
    const textY = chart.height / 2;
    ctx.fillText(t, textX, textY);
    ctx.save();
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const HorizontalBarChartTooltipPlugin = (context: any) => {
  const { chart, tooltip } = context;

  if (chart.canvas.parentNode === null) {
    return;
  }
  // Generate tooltip
  const element = document.createElement("div");
  element.id = "order-status-tooltip";
  element.style.backgroundColor = "#fff";
  element.style.color = "#000";
  element.style.borderRadius = "3px";
  element.style.boxShadow = "0px 4px 6px rgba(16, 24, 40, 0.03)";
  element.style.opacity = "1";
  element.style.pointerEvents = "none";
  element.style.position = "absolute";
  // element.style.transform = 'translate(-50%, 0)';
  element.style.transition = "all .1s ease";
  element.style.display = "flex";
  element.style.flexDirection = "row";
  element.style.gap = "10px";
  element.style.padding = "4px";
  const label = document.createElement("div");
  label.style.fontSize = "12px";
  label.style.lineHeight = "18px";
  label.style.fontWeight = "600";
  label.style.color = "#0F172A";
  label.innerText =
    Array.isArray(tooltip.title) && tooltip.title.length > 0
      ? tooltip.title[0]
      : "";
  const value = document.createElement("div");
  value.style.fontSize = "12px";
  value.style.lineHeight = "18px";
  value.style.fontWeight = "500";
  value.style.color = "#475569";
  value.innerText =
    Array.isArray(tooltip.title) && tooltip.title.length > 0
      ? tooltip.dataPoints[0].raw.toFixed(0).toString()
      : "";
  element.appendChild(label);
  element.appendChild(value);
  const previous = chart.canvas.parentNode.querySelector(
    "#order-status-tooltip"
  );
  if (previous) {
    chart.canvas.parentNode.removeChild(previous);
  }
  chart.canvas.parentNode.appendChild(element);
  if (tooltip.opacity === 0) {
    element.style.opacity = "0";
    return;
  }
  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
  element.style.opacity = "1";
  element.style.left = positionX + tooltip.caretX + "px";
  element.style.top = positionY + tooltip.caretY - 16 + "px";
  element.style.padding =
    tooltip.options.padding + "px " + tooltip.options.padding + "px";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RadarChartTooltipPlugin = (context: any) => {
  const { chart, tooltip } = context;
  if (chart.canvas.parentNode === null) {
    return;
  }
  // Generate tooltip
  const element = document.createElement("div");
  element.id = "radar-tooltip";
  element.style.backgroundColor = "#fff";
  element.style.color = "#000";
  element.style.borderRadius = "3px";
  element.style.boxShadow = "0px 4px 6px rgba(16, 24, 40, 0.03)";
  element.style.opacity = "1";
  element.style.pointerEvents = "none";
  element.style.position = "absolute";
  element.style.transform = "translate(-50%, 0)";
  element.style.transition = "all .1s ease";
  element.style.display = "flex";
  element.style.flexDirection = "row";
  element.style.gap = "10px";
  element.style.padding = "4px";
  const label = document.createElement("div");
  label.style.fontSize = "12px";
  label.style.lineHeight = "18px";
  label.style.fontWeight = "600";
  label.style.color = "#0F172A";
  label.innerText = tooltip.title.length > 0 ? tooltip.title[0] : "";
  const value = document.createElement("div");
  value.style.fontSize = "12px";
  value.style.lineHeight = "18px";
  value.style.fontWeight = "500";
  value.style.color = "#475569";
  value.innerText =
    tooltip.title.length > 0 ? tooltip.y.toFixed(0).toString() : "";
  element.appendChild(label);
  element.appendChild(value);
  const previous = chart.canvas.parentNode.querySelector("#radar-tooltip");
  if (previous) {
    chart.canvas.parentNode.removeChild(previous);
  }
  chart.canvas.parentNode.appendChild(element);
  if (tooltip.opacity === 0) {
    element.style.opacity = "0";
    return;
  }
  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
  element.style.opacity = "1";
  element.style.left = positionX + tooltip.caretX + "px";
  element.style.top = positionY + tooltip.caretY + "px";
  element.style.padding =
    tooltip.options.padding + "px " + tooltip.options.padding + "px";
};

const DashboardOrdersStatusGraph = ({ date }: StatsFilterProps) => {
  const { data: statsCommandsStatus } = useCommandsStatusStats({ date });
  const ref = useRef(null);
  const chart = useRef<Chart | null>(null);

  const x = useMemo(() => {
    if (statsCommandsStatus === null || statsCommandsStatus === undefined) {
      return [];
    }
    return [
      CommandStatus.DRAFT,
      CommandStatus.WAITING_FOR_RECEPTION,
      CommandStatus.RECEIVED,
      CommandStatus.MODELING,
      CommandStatus.PRINTING,
      CommandStatus.INJECTING,
      CommandStatus.POLISHING,
      CommandStatus.QUALITY_CONTROL,
      CommandStatus.READY_FOR_DELIVERY,
      CommandStatus.IN_DELIVERY,
      CommandStatus.ABORTED,
      CommandStatus.PENDING,
    ].map((status) => statsCommandsStatus[status]);
  }, [statsCommandsStatus]);
  const y = useMemo(
    () => [
      i18n.t(`command.status.${CommandStatus.DRAFT}`),
      i18n.t(`command.status.${CommandStatus.WAITING_FOR_RECEPTION}`),
      i18n.t(`command.status.${CommandStatus.RECEIVED}`),
      i18n.t(`command.status.${CommandStatus.MODELING}`),
      i18n.t(`command.status.${CommandStatus.PRINTING}`),
      i18n.t(`command.status.${CommandStatus.INJECTING}`),
      i18n.t(`command.status.${CommandStatus.POLISHING}`),
      i18n.t(`command.status.${CommandStatus.QUALITY_CONTROL}`),
      i18n.t(`command.status.${CommandStatus.READY_FOR_DELIVERY}`),
      i18n.t(`command.status.${CommandStatus.IN_DELIVERY}`),
      i18n.t(`command.status.${CommandStatus.ABORTED}`),
      i18n.t(`command.status.${CommandStatus.PENDING}`),
    ],
    []
  );
  const c = useMemo(
    () => [
      "#E2E8F0",
      "#EEF5FF",
      "#BCD9FF",
      "#8EC2FF",
      "#599FFF",
      "#2B75FF",
      "#1B58F5",
      "#1444E1",
      "#1737B6",
      "#1737B6",
      "#19338F",
      "#19338F",
    ],
    []
  );

  useEffect(() => {
    if (ref.current !== null && chart.current === null) {
      chart.current = new Chart(ref.current, {
        type: "bar",
        data: {
          labels: y,
          datasets: [
            {
              data: x,
              borderWidth: 1,
              borderRadius: 4,
              backgroundColor: c,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: "nearest",
              external: HorizontalBarChartTooltipPlugin,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
            },
            x: {
              display: false,
            },
          },
          responsive: true,
          maintainAspectRatio: true,
          indexAxis: "y",
        },
      });
    }
    if (ref.current !== null && chart.current !== null) {
      const instance = chart.current;
      // X Update
      instance.data.datasets[0].data = x;
      // Y No update (static)
      // C No update (static)
      instance.update();
    }
  }, [x, y, c]);
  return (
    <Box w="100%">
      <canvas ref={ref} width="100%" />
    </Box>
  );
};

const DashboardCommandTypePieGraph = ({ date }: StatsFilterProps) => {
  const ref = useRef(null);
  const chart = useRef<Chart | null>(null);
  const { data: statsCommandsTypes } = useCommandsTypesStats({ date });

  const x = useMemo(() => {
    if (statsCommandsTypes === undefined) {
      return [];
    }
    return [
      CommandType.POSTE,
      CommandType.THREED,
      CommandType.OTOSCAN,
      "REWORK",
    ].map((type) => statsCommandsTypes[type] ?? 0);
  }, [statsCommandsTypes]);
  const y = useMemo(
    () => [
      "Envoi postal",
      "Envoi scan",
      "Envoi otoscan",
      "Refonte anciennes empreintes",
    ],
    []
  );
  const c = useMemo(() => ["#1B58F5", "#2B75FF", "#599FFF", "#E2E8F0"], []);
  useEffect(() => {
    if (ref.current !== null && chart.current === null) {
      chart.current = new Chart(ref.current, {
        type: "doughnut",
        options: {
          responsive: false,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
        },
        plugins: [
          PieChartCenterTextPlugin((chart: Chart) => {
            const data = chart.data.datasets[0].data as number[];
            if (data === null) {
              return "";
            }
            return data
              .reduce((current, value) => current + value, 0)
              .toString();
          }),
        ],
        data: {
          labels: y,
          datasets: [
            {
              data: x,
              backgroundColor: c,
              hoverOffset: 4,
            },
          ],
        },
      });
    }
    if (ref.current !== null && chart.current !== null) {
      const instance = chart.current;
      // X Update
      instance.data.datasets[0].data = x;
      // Y No update (static)
      // C No update (static)
      instance.update();
    }
  }, [x, y, c]);
  return (
    <Flex direction="row" alignItems="start">
      <canvas ref={ref} />
      <Flex direction="column" pl="20px">
        <Flex gap="8px" direction="row" alignItems="center">
          <Icon
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
            w="8px"
            h="8px"
          >
            <circle cx="50" cy="50" r="50" fill="#1B58F5" />
          </Icon>
          Envoi postal
        </Flex>
        <Flex gap="8px" direction="row" alignItems="center">
          <Icon
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
            w="8px"
            h="8px"
          >
            <circle cx="50" cy="50" r="50" fill="#2B75FF" />
          </Icon>
          Envoi scan
        </Flex>
        <Flex gap="8px" direction="row" alignItems="center">
          <Icon
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
            w="8px"
            h="8px"
          >
            <circle cx="50" cy="50" r="50" fill="#599FFF" />
          </Icon>
          Envoi otoscan
        </Flex>
        <Flex gap="8px" direction="row" alignItems="center">
          <Icon
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
            w="8px"
            h="8px"
          >
            <circle cx="50" cy="50" r="50" fill="#E2E8F0" />
          </Icon>
          Refonte anciennes empreintes
        </Flex>
      </Flex>
    </Flex>
  );
};

const DashboardCommandEarmoldTypePieGraph = ({ date }: StatsFilterProps) => {
  const ref = useRef(null);
  const chart = useRef<Chart | null>(null);
  const { data: statsCommandsEarmoldTypes } = useCommandsEarmoldTypesStats({
    date,
  });

  const x = useMemo(() => {
    if (
      statsCommandsEarmoldTypes === undefined ||
      !Array.isArray(statsCommandsEarmoldTypes)
    ) {
      return [];
    }
    return statsCommandsEarmoldTypes
      .filter((scet) => scet.earmold.id !== null)
      .map((scet) => scet.value);
  }, [statsCommandsEarmoldTypes]);
  const y = useMemo(() => {
    if (
      statsCommandsEarmoldTypes === undefined ||
      !Array.isArray(statsCommandsEarmoldTypes)
    ) {
      return [];
    }
    return statsCommandsEarmoldTypes
      .filter((scet) => scet.earmold.id !== null)
      .map((scet) => scet.earmold.label);
  }, [statsCommandsEarmoldTypes]);
  const c = useMemo(
    () => [
      "#E2E8F0",
      "#EEF5FF",
      "#BCD9FF",
      "#8EC2FF",
      "#599FFF",
      "#2B75FF",
      "#1B58F5",
      "#1444E1",
      "#1737B6",
      "#19338F",
    ],
    []
  );
  useEffect(() => {
    if (ref.current !== null && chart.current === null) {
      chart.current = new Chart(ref.current, {
        type: "doughnut",
        options: {
          responsive: false,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
        },
        plugins: [
          PieChartCenterTextPlugin((chart: Chart) => {
            const data = chart.data.datasets[0].data as number[];
            if (data === null) {
              return "";
            }
            return data
              .reduce((current, value) => current + value, 0)
              .toString();
          }),
        ],
        data: {
          labels: y,
          datasets: [
            {
              data: x,
              backgroundColor: c,
              hoverOffset: 4,
            },
          ],
        },
      });
    }
    if (ref.current !== null && chart.current !== null) {
      const instance = chart.current;
      // X Update
      instance.data.datasets[0].data = x;
      // Y Update
      instance.data.labels = y;
      // C No update (static)
      instance.update();
    }
  }, [x, y, c]);
  return (
    <Flex direction="row" alignItems="start">
      <canvas ref={ref} />
      <Flex direction="column" pl="20px">
        {Array.isArray(y) &&
          y.map((elem, index) => (
            <Flex gap="8px" direction="row" alignItems="center" key={index}>
              <Icon
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
                w="8px"
                h="8px"
              >
                <circle cx="50" cy="50" r="50" fill={c[index]} />
              </Icon>
              {elem}
            </Flex>
          ))}
      </Flex>
    </Flex>
  );
};

const DashboardTicketTypeRadarGraph = ({ date }: StatsFilterProps) => {
  const ref = useRef(null);
  const chart = useRef<Chart | null>(null);
  const { data: statsTicketsTypes } = useTicketsTypesStats({ date });

  const x = useMemo(() => {
    if (statsTicketsTypes === undefined || !Array.isArray(statsTicketsTypes)) {
      return [];
    }
    return statsTicketsTypes.map(
      (stt: { label: string; value: number }) => stt.value
    );
  }, [statsTicketsTypes]);
  const y = useMemo(() => {
    if (statsTicketsTypes === undefined || !Array.isArray(statsTicketsTypes)) {
      return [];
    }
    return statsTicketsTypes.map(
      (stt: { label: string; value: number }) => stt.label
    );
  }, [statsTicketsTypes]);
  const c = useMemo(() => ["#1B58F5", "#2B75FF", "#599FFF", "#E2E8F0"], []);
  useEffect(() => {
    if (ref.current !== null && chart.current === null) {
      chart.current = new Chart(ref.current, {
        type: "radar",
        data: {
          labels: y,
          datasets: [
            {
              data: x,
              fill: true,
              backgroundColor: "#D9E9FF",
              borderColor: "#2B75FF",
              pointBackgroundColor: "#2B75FF",
              pointBorderColor: "#fff",
              pointHoverBackgroundColor: "#fff",
              pointHoverBorderColor: "#2B75FF",
            },
          ],
        },
        options: {
          elements: {
            line: {
              borderWidth: 3,
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: "nearest",
              external: RadarChartTooltipPlugin,
            },
          },
          scales: {
            r: {
              ticks: {
                display: false,
              },
            },
          },
        },
      });
    }
    if (ref.current !== null && chart.current !== null) {
      const instance = chart.current;
      // X Update
      instance.data.datasets[0].data = x;
      // Y Update
      instance.data.labels = y;
      // C No update (static)
      instance.update();
    }
  }, [x, y, c]);
  return (
    <Flex
      w="100%"
      gap="16px"
      justify="center"
      alignItems="center"
      height="366px"
    >
      <canvas ref={ref} />
    </Flex>
  );
};

const DashboardCommandTreatmentTimeBarGraph = ({ date }: StatsFilterProps) => {
  const { data: statsCommandTreatmentTime } = useCommandsTreatmentTimeStats({
    date,
  });
  const ref = useRef(null);
  const chart = useRef<Chart | null>(null);

  const x = useMemo(() => {
    if (
      statsCommandTreatmentTime === null ||
      statsCommandTreatmentTime === undefined
    ) {
      return [];
    }
    return [
      CommandStatus.RECEIVED,
      CommandStatus.MODELING,
      CommandStatus.PRINTING,
      CommandStatus.POLISHING,
      CommandStatus.READY_FOR_DELIVERY,
      CommandStatus.DELIVERED,
    ].map((status) => statsCommandTreatmentTime[status].p50?.days ?? 0);
  }, [statsCommandTreatmentTime]);
  const y = useMemo(
    () => [
      "Receptionné",
      "En modélisation",
      "En impression",
      "En finition",
      "Prêt à livrer",
      "Expédié",
    ],
    []
  );

  useEffect(() => {
    if (ref.current !== null && chart.current === null) {
      chart.current = new Chart(ref.current, {
        type: "bar",
        data: {
          labels: y,
          datasets: [
            {
              data: x,
              borderWidth: 1,
              borderRadius: 4,
              backgroundColor: "#1B58F5",
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `${context.raw} j`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return `${value} j`;
                }
              }
            },
          },
          responsive: true,
          maintainAspectRatio: true,
          indexAxis: "x",
        },
      });
    }
    if (ref.current !== null && chart.current !== null) {
      const instance = chart.current;
      // X Update
      instance.data.datasets[0].data = x;
      // Y No update (static)
      // C No update (static)
      instance.update();
    }
  }, [x, y]);
  return (
    <Box w="100%">
      <canvas ref={ref} width="100%" />
    </Box>
  );
};

const DashboardPodium = ({ date }: StatsFilterProps) => {
  const { data: statsCommandsCenters } = useCommandsCentersStats({ date });
  const navigate = useNavigate();

  if (
    statsCommandsCenters === undefined ||
    !Array.isArray(statsCommandsCenters)
  ) {
    return <></>;
  }
  return statsCommandsCenters.slice(0, 5).map((stat, index) => (
    <Flex
      key={stat.center.id}
      w="100%"
      justify="space-between"
      py="16px"
      borderTop={index !== 0 ? "solid 1px" : undefined}
      borderColor="gray.100"
      onClick={() => {
        if (stat.center.id) {
          navigate(`/contacts/centers/${stat.center.id}`);
        }
      }}
    >
      <Text
        textTransform="capitalize"
        color="brand.800"
        lineHeight="21px"
        fontSize="14px"
        fontWeight="600"
        _hover={{ textDecoration: "underline" }}
      >
        {stat.center.name}
      </Text>
      <Text lineHeight="18px" fontSize="12px" fontWeight="500" color="gray.900">
        {stat.value} commandes
      </Text>
    </Flex>
  ));
};

function DashboardPageAdmin() {
  const [date, setDate] = useState<Date | undefined>(undefined);
  const { data: announcement } = useGlobalAnnouncement();
  const { data: statsCommandsStatus } = useCommandsStatusStats({ date });
  const { data: statsCommandTreatmentTime } = useCommandsTreatmentTimeStats({
    date,
  });

  const mergeCommandTreatmentTimeOnStatus = (statuses: CommandStatus[]) => {
    const ret: CommandTreatmentTimeStats = {
      average: {
        years: 0,
        month: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      },
      p50: {
        years: 0,
        month: 0,
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      },
    };
    if (statsCommandTreatmentTime === undefined) {
      return ret;
    }
    statuses.forEach((status) => {
      const stats = statsCommandTreatmentTime[status];
      if (stats.average && ret.average) {
        ret.average.years =
          (ret.average.years ?? 0) + (stats.average.years ?? 0);
        ret.average.month =
          (ret.average.month ?? 0) + (stats.average.month ?? 0);
        ret.average.days = (ret.average.days ?? 0) + (stats.average.days ?? 0);
        ret.average.hours =
          (ret.average.hours ?? 0) + (stats.average.hours ?? 0);
        ret.average.minutes =
          (ret.average.minutes ?? 0) + (stats.average.minutes ?? 0);
        ret.average.seconds =
          (ret.average.seconds ?? 0) + (stats.average.seconds ?? 0);
        ret.average.milliseconds =
          (ret.average.milliseconds ?? 0) + (stats.average.milliseconds ?? 0);
      }
      if (stats.p50 && ret.p50) {
        ret.p50.years = (ret.p50.years ?? 0) + (stats.p50.years ?? 0);
        ret.p50.month = (ret.p50.month ?? 0) + (stats.p50.month ?? 0);
        ret.p50.days = (ret.p50.days ?? 0) + (stats.p50.days ?? 0);
        ret.p50.hours = (ret.p50.hours ?? 0) + (stats.p50.hours ?? 0);
        ret.p50.minutes = (ret.p50.minutes ?? 0) + (stats.p50.minutes ?? 0);
        ret.p50.seconds = (ret.p50.seconds ?? 0) + (stats.p50.seconds ?? 0);
        ret.p50.milliseconds =
          (ret.p50.milliseconds ?? 0) + (stats.p50.milliseconds ?? 0);
      }
    });
    return ret;
  };
  const commandTreatmentTimeInDays = (treatment: CommandTreatmentTimeStats) => {
    const avg = treatment.average;
    let ret =
      (avg?.years ?? 0) * 365 + (avg?.month ?? 0) * 30.5 + (avg?.days ?? 0);
    if (avg?.hours || avg?.minutes || avg?.seconds || avg?.milliseconds) {
      ret += 1;
    }
    return ret;
  };

  return (
    <Flex direction="column">
      <Flex w="100%" justify="space-between" mb="24px">
        <PageTitle>Bonjour</PageTitle>
        <Box w="400px">
          <InputSingleDatePicker
            placeholder="Sélectionner une date"
            date={date}
            setDate={setDate}
          />
        </Box>
      </Flex>

      {announcement !== undefined && announcement !== null && (
        <Flex
          direction="column"
          my="32px"
          p="32px"
          gap="8px"
          backgroundColor="brand.50"
          border="solid 1px"
          borderColor="gray.300"
          borderRadius="xl"
          boxShadow="xs"
        >
          <Text
            lineHeight="18px"
            fontSize="12px"
            fontWeight="400"
            color="gray.700"
          >
            A la une
          </Text>
          <Text
            lineHeight="36px"
            fontSize="24px"
            fontWeight="600"
            color="gray.800"
          >
            {announcement.title}
          </Text>
          <Text
            lineHeight="21px"
            fontSize="14px"
            fontWeight="400"
            color="gray.600"
          >
            {announcement.content}
          </Text>
        </Flex>
      )}

      <Grid gridTemplateColumns={"1fr 1fr 1fr 1fr"} gap="24px">
        <DashboardCard
          title="Commandes"
          value={
            statsCommandsStatus
              ? Object.values(statsCommandsStatus).reduce((p, v) => p + v, 0)
              : "--"
          }
        />
        <DashboardCard
          title="Temps de réception moy."
          value={
            commandTreatmentTimeInDays(
              mergeCommandTreatmentTimeOnStatus([
                CommandStatus.WAITING_FOR_RECEPTION,
              ])
            ) + " j"
          }
        />
        <DashboardCard
          title="Temps de fabrication moy."
          value={
            commandTreatmentTimeInDays(
              mergeCommandTreatmentTimeOnStatus([
                CommandStatus.MODELING,
                CommandStatus.PRINTING,
                CommandStatus.INJECTING,
                CommandStatus.POLISHING,
                CommandStatus.QUALITY_CONTROL,
                CommandStatus.READY_FOR_DELIVERY,
              ])
            ) + " j"
          }
        />
        <DashboardCard
          title="Temps d'expédition moy."
          value={
            commandTreatmentTimeInDays(
              mergeCommandTreatmentTimeOnStatus([CommandStatus.IN_DELIVERY])
            ) + " j"
          }
        />
      </Grid>

      <Grid gridTemplateColumns={"1fr 1fr"} gap="24px" mt="24px">
        <DashboardCardWithHeader header="Commandes" px="24px">
          <DashboardOrdersStatusGraph date={date} />
        </DashboardCardWithHeader>
        <DashboardCardWithHeader header="Podium" px="24px">
          <DashboardPodium date={date} />
        </DashboardCardWithHeader>
        <DashboardCardWithHeader header="Commandes" direction="row" px="24px">
          <DashboardCommandTypePieGraph date={date} />
        </DashboardCardWithHeader>
        <DashboardCardWithHeader header="Types des tickets">
          <DashboardTicketTypeRadarGraph date={date} />
        </DashboardCardWithHeader>
        <DashboardCardWithHeader
          header="Produits commandés"
          direction="row"
          px="24px"
        >
          <DashboardCommandEarmoldTypePieGraph date={date} />
        </DashboardCardWithHeader>
        <DashboardCardWithHeader header="Temps de traitement" px="24px">
          <DashboardCommandTreatmentTimeBarGraph date={date} />
        </DashboardCardWithHeader>
      </Grid>
    </Flex>
  );
}

const DashboardPageCenterLastCommandsTable = () => {
  const { center } = useCenters();
  const commands = useCenterCommandsPaginated({ id: center?.id })(
    1,
    5,
    undefined,
    { not_status: CommandStatus.DRAFT },
    ["-createdAt"]
  );

  const columns = useMemo<ColumnDef<Command>[]>(
    () => [
      {
        accessorFn: (row) => (
          <Tooltip content={`${row.exposedId}`}>
            <Box
              textOverflow={"ellipsis"}
              whiteSpace={"nowrap"}
              overflow={"hidden"}
              fontSize={"12px"}
            >
              {row.exposedId}
            </Box>
          </Tooltip>
        ),
        id: "id",
        header: "ID",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => (
          <Box
            textOverflow={"ellipsis"}
            whiteSpace={"nowrap"}
            overflow={"hidden"}
            fontSize={"12px"}
          >{`${row.patient?.firstName} ${row.patient?.lastName}`}</Box>
        ),
        id: "patient",
        header: "Patient",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => (
          <OrderStatusBadge halted={row.haltedAt != null} status={row.status} />
        ),
        id: "status",
        header: "Statut",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => <CommandTypeBadge type={row.type} />,
        id: "type",
        header: "Type",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => (
          <Box
            textOverflow={"ellipsis"}
            whiteSpace={"nowrap"}
            overflow={"hidden"}
            fontSize={"12px"}
          >
            {format(row.createdAt, "dd/MM/yyyy")}
          </Box>
        ),
        id: "created_at",
        header: "Création",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => (
          <Box
            textOverflow={"ellipsis"}
            whiteSpace={"nowrap"}
            overflow={"hidden"}
            fontSize={"12px"}
          >
            {row.deliveredAt ? format(row.deliveredAt, "dd/MM/yyyy") : "--"}
          </Box>
        ),
        id: "delivered_at",
        header: "Livraison",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
      {
        accessorFn: (row) => <InlineUserAvatar id={row.creatorId} />,
        id: "creator",
        header: "Créé par",
        cell: (info) => info.getValue(),
        sortDescFirst: true,
      },
    ],
    []
  );
  const data = useMemo(() => {
    if (commands === null || commands === undefined) {
      return [];
    }
    return commands.data;
  }, [commands]);
  const table = useReactTable({
    data: data,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <>
      <Flex justify="space-between" pt={8}>
        <Text lineHeight="30px" fontSize="20px" fontWeight="600" color="black">
          Dernières commandes
        </Text>
        <NavLink to="/orders">
          <Button variant="outline">Voir tout</Button>
        </NavLink>
      </Flex>
      <ClientSideTable
        table={table}
        w={["200px", "auto", "200px", "200px", "100px", "100px", "200px"]}
        flexGrow={[0, 1, 0, 0, 0, 0]}
        noSearch
        noPagination
      />
    </>
  );
};

const DashboardCenterOrdersStatusGraph = ({ date }: StatsFilterProps) => {
  const { center } = useCenters();
  const { data: statsCommandsStatus } = useCenterCommandsStatusStats({
    id: center?.id,
    date,
  });
  const ref = useRef(null);
  const chart = useRef<Chart | null>(null);

  const x = useMemo(() => {
    if (statsCommandsStatus === null || statsCommandsStatus === undefined) {
      return [];
    }
    return [
      CommandStatus.DRAFT,
      CommandStatus.WAITING_FOR_RECEPTION,
      CommandStatus.RECEIVED,
      CommandStatus.MODELING,
      CommandStatus.PRINTING,
      CommandStatus.POLISHING,
      CommandStatus.READY_FOR_DELIVERY,
      CommandStatus.IN_DELIVERY,
      CommandStatus.ABORTED,
      CommandStatus.PENDING,
    ].map((status) => statsCommandsStatus[status]);
  }, [statsCommandsStatus]);
  const y = useMemo(
    () => [
      i18n.t(`command.status.${CommandStatus.DRAFT}`),
      i18n.t(`command.status.${CommandStatus.WAITING_FOR_RECEPTION}`),
      i18n.t(`command.status.${CommandStatus.RECEIVED}`),
      i18n.t(`command.status.${CommandStatus.MODELING}`),
      i18n.t(`command.status.${CommandStatus.PRINTING}`),
      i18n.t(`command.status.${CommandStatus.POLISHING}`),
      i18n.t(`command.status.${CommandStatus.READY_FOR_DELIVERY}`),
      i18n.t(`command.status.${CommandStatus.IN_DELIVERY}`),
      i18n.t(`command.status.${CommandStatus.ABORTED}`),
      i18n.t(`command.status.${CommandStatus.PENDING}`),
    ],
    []
  );
  const c = useMemo(
    () => [
      "#E2E8F0",
      "#EEF5FF",
      "#BCD9FF",
      "#8EC2FF",
      "#599FFF",
      "#2B75FF",
      "#1B58F5",
      "#1444E1",
      "#1737B6",
      "#19338F",
    ],
    []
  );

  useEffect(() => {
    if (ref.current !== null && chart.current === null) {
      chart.current = new Chart(ref.current, {
        type: "bar",
        data: {
          labels: y,
          datasets: [
            {
              data: x,
              borderWidth: 1,
              borderRadius: 4,
              backgroundColor: c,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: "nearest",
              external: HorizontalBarChartTooltipPlugin,
            },
          },
          scales: {
            y: {
              beginAtZero: true,
            },
            x: {
              display: false,
            },
          },
          responsive: true,
          maintainAspectRatio: true,
          indexAxis: "y",
        },
      });
    }
    if (ref.current !== null && chart.current !== null) {
      const instance = chart.current;
      // X Update
      instance.data.datasets[0].data = x;
      // Y No update (static)
      // C No update (static)
      instance.update();
    }
  }, [x, y, c]);
  return (
    <Box w="100%">
      <canvas ref={ref} width="100%" />
    </Box>
  );
};

const DashboardCenterCommandEarmoldTypePieGraph = ({
  date,
}: StatsFilterProps) => {
  const ref = useRef(null);
  const chart = useRef<Chart | null>(null);
  const { center } = useCenters();
  const { data: statsCommandsEarmoldTypes } =
    useCenterCommandsEarmoldTypesStats({
      id: center?.id,
      date,
    });

  const x = useMemo(() => {
    if (
      !statsCommandsEarmoldTypes ||
      !Array.isArray(statsCommandsEarmoldTypes)
    ) {
      return [];
    }
    return statsCommandsEarmoldTypes
      .filter((scet) => scet.earmold.id !== null)
      .map((scet) => scet.value);
  }, [statsCommandsEarmoldTypes]);
  const y = useMemo(() => {
    if (
      !statsCommandsEarmoldTypes ||
      !Array.isArray(statsCommandsEarmoldTypes)
    ) {
      return [];
    }
    return statsCommandsEarmoldTypes
      .filter((scet) => scet.earmold.id !== null)
      .map((scet) => scet.earmold.label);
  }, [statsCommandsEarmoldTypes]);
  const c = useMemo(
    () => [
      "#E2E8F0",
      "#EEF5FF",
      "#BCD9FF",
      "#8EC2FF",
      "#599FFF",
      "#2B75FF",
      "#1B58F5",
      "#1444E1",
      "#1737B6",
      "#19338F",
    ],
    []
  );
  useEffect(() => {
    if (ref.current !== null && chart.current === null) {
      chart.current = new Chart(ref.current, {
        type: "doughnut",
        options: {
          responsive: false,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
          },
        },
        plugins: [
          PieChartCenterTextPlugin((chart: Chart) => {
            const data = chart.data.datasets[0].data as number[];
            if (data === null) {
              return "";
            }
            return data
              .reduce((current, value) => current + value, 0)
              .toString();
          }),
        ],
        data: {
          labels: y,
          datasets: [
            {
              data: x,
              backgroundColor: c,
              hoverOffset: 4,
            },
          ],
        },
      });
    }
    if (ref.current !== null && chart.current !== null) {
      const instance = chart.current;
      // X Update
      instance.data.datasets[0].data = x;
      // Y Update
      instance.data.labels = y;
      // C No update (static)
      instance.update();
    }
  }, [x, y, c]);
  return (
    <Flex direction="row" alignItems="start">
      <canvas ref={ref} />
      <Flex direction="column" pl="20px">
        {Array.isArray(y) &&
          y.map((elem, index) => (
            <Flex
              key={elem + index}
              gap="8px"
              direction="row"
              alignItems="center"
            >
              <Icon
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
                w="8px"
                h="8px"
              >
                <circle cx="50" cy="50" r="50" fill={c[index]} />
              </Icon>
              {elem}
            </Flex>
          ))}
      </Flex>
    </Flex>
  );
};

const DashboardCenterTicketTypeRadarGraph = ({ date }: StatsFilterProps) => {
  const ref = useRef(null);
  const chart = useRef<Chart | null>(null);
  const { center } = useCenters();
  const { data: statsTicketsTypes } = useCenterTicketsTypesStats({
    id: center?.id,
    date,
  });

  const x = useMemo(() => {
    if (!statsTicketsTypes || !Array.isArray(statsTicketsTypes)) {
      return [];
    }
    return statsTicketsTypes.map(
      (stt: { label: string; value: number }) => stt.value
    );
  }, [statsTicketsTypes]);
  const y = useMemo(() => {
    if (!statsTicketsTypes || !Array.isArray(statsTicketsTypes)) {
      return [];
    }
    return statsTicketsTypes.map(
      (stt: { label: string; value: number }) => stt.label
    );
  }, [statsTicketsTypes]);
  const c = useMemo(() => ["#1B58F5", "#2B75FF", "#599FFF", "#E2E8F0"], []);
  useEffect(() => {
    if (ref.current !== null && chart.current === null) {
      chart.current = new Chart(ref.current, {
        type: "radar",
        data: {
          labels: y,
          datasets: [
            {
              data: x,
              fill: true,
              backgroundColor: "#D9E9FF",
              borderColor: "#2B75FF",
              pointBackgroundColor: "#2B75FF",
              pointBorderColor: "#fff",
              pointHoverBackgroundColor: "#fff",
              pointHoverBorderColor: "#2B75FF",
            },
          ],
        },
        options: {
          elements: {
            line: {
              borderWidth: 3,
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: "nearest",
              external: RadarChartTooltipPlugin,
            },
          },
          scales: {
            r: {
              ticks: {
                display: false,
              },
            },
          },
        },
      });
    }
    if (ref.current !== null && chart.current !== null) {
      const instance = chart.current;
      // X Update
      instance.data.datasets[0].data = x;
      // Y Update
      instance.data.labels = y;
      // C No update (static)
      instance.update();
    }
  }, [x, y, c]);
  return (
    <Flex
      w="100%"
      gap="16px"
      justify="center"
      alignItems="center"
      height="366px"
    >
      <canvas ref={ref} />
    </Flex>
  );
};

const DashboardPageCenter = () => {
  const [date, setDate] = useState<Date | undefined>(undefined);
  const user = useConnectedUser();
  const { data: announcement } = useGlobalAnnouncement();
  const { center } = useCenters();
  const { data: centerPatientsStats } = useCenterPatientsStats({
    id: center?.id,
  });
  const isSmallDevice = useMediaQuery("only screen and (max-width : 768px)");

  return (
    <Flex direction="column">
      <Flex w="100%" justify="space-between" mb="4px">
        <PageTitle>Bonjour {user?.firstName},</PageTitle>
      </Flex>

      {announcement !== undefined && announcement !== null && (
        <Flex
          direction="column"
          my="32px"
          p="32px"
          gap="8px"
          backgroundColor="brand.50"
          border="solid 1px"
          borderColor="gray.300"
          borderRadius="xl"
          boxShadow="xs"
        >
          <Text
            lineHeight="18px"
            fontSize="12px"
            fontWeight="400"
            color="gray.700"
          >
            A la une
          </Text>
          <Text
            lineHeight="36px"
            fontSize="24px"
            fontWeight="600"
            color="gray.800"
          >
            {announcement.title}
          </Text>
          <Text
            lineHeight="21px"
            fontSize="14px"
            fontWeight="400"
            color="gray.600"
          >
            {announcement.content}
          </Text>
        </Flex>
      )}

      <Flex w="100%" justify="space-between" mb="24px">
        {!isSmallDevice && (
          <Text
            lineHeight="36px"
            fontSize="24px"
            fontWeight="600"
            color="gray.800"
          >
            Tableau de bord
          </Text>
        )}
        <Box w="400px">
          <InputSingleDatePicker
            placeholder="Choisir une date"
            date={date}
            setDate={setDate}
          />
        </Box>
      </Flex>

      <Grid
        templateColumns={{
          base: "repeat(2, 1fr)",
          xl: "repeat(4, 1fr)",
        }}
        gap="24px"
      >
        <DashboardCard
          title="Nombre total de patients"
          value={
            !centerPatientsStats?.patients
              ? "--"
              : centerPatientsStats.patients.count
          }
        />
        <DashboardCard title="Temps de réception moy." value="--" />
        <DashboardCard title="Temps de fabrication moy." value="--" />
        <DashboardCard title="Temps de transport" value="--" />
      </Grid>

      <Grid
        templateColumns={{
          base: "repeat(1, 1fr)",
          xl: "repeat(2, 1fr)",
        }}
        gap="24px"
        mt="24px"
      >
        <DashboardCardWithHeader header="Commandes" px="24px">
          <DashboardCenterOrdersStatusGraph date={date} />
        </DashboardCardWithHeader>
        <DashboardCardWithHeader header="Colis en transit" px="24px">
          ---
        </DashboardCardWithHeader>
        <DashboardCardWithHeader
          header="Produits commandés"
          direction="row"
          px="24px"
        >
          <DashboardCenterCommandEarmoldTypePieGraph date={date} />
        </DashboardCardWithHeader>
        <DashboardCardWithHeader header="Types des tickets">
          <DashboardCenterTicketTypeRadarGraph date={date} />
        </DashboardCardWithHeader>
      </Grid>

      {!isSmallDevice && <DashboardPageCenterLastCommandsTable />}
    </Flex>
  );
};

function DashboardPage() {
  const user = useConnectedUser();

  if (user?.isSuperAdmin) {
    return <DashboardPageAdmin />;
  }
  return <DashboardPageCenter />;
}

export default DashboardPage;
